import React, { useEffect, useRef, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule'
import Divider from '@mui/material/Divider'
import maplibregl from 'maplibre-gl'

const MIN_ZOOM = 3.5

type Props = {
  hereMap: H.Map | undefined
  mlMap?: maplibregl.Map | null
}

const TOOLTIP_TAP_DURATION = 1500

export function ZoomControls({ hereMap, mlMap }: Props) {
  // Controlled tooltip so a mobile tap shows the message too, not just a
  // long-press or desktop hover (SH-1884).
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const tooltipCloseTimer = useRef<ReturnType<typeof setTimeout>>()
  // Was the current zoom-in triggered by a finger tap (vs a desktop mouse
  // click)? Only taps should trigger the timed tooltip; on desktop the tooltip
  // already shows on hover and the timer would wrongly close it mid-hover.
  const lastPointerWasTouch = useRef(false)

  useEffect(() => () => clearTimeout(tooltipCloseTimer.current), [])

  const showTooltipOnTap = () => {
    setTooltipOpen(true)
    clearTimeout(tooltipCloseTimer.current)
    tooltipCloseTimer.current = setTimeout(
      () => setTooltipOpen(false),
      TOOLTIP_TAP_DURATION,
    )
  }

  const handleZoomIn = () => {
    if (mlMap) {
      mlMap.easeTo({ zoom: Math.min(mlMap.getZoom() + 1, mlMap.getMaxZoom()) })
      if (lastPointerWasTouch.current) {
        showTooltipOnTap()
      }
      return
    }
    if (hereMap) {
      const currentZoom = hereMap.getZoom()
      hereMap.setZoom(currentZoom + 1, true)
    }
  }

  const handleZoomOut = () => {
    if (mlMap) {
      mlMap.easeTo({ zoom: Math.max(mlMap.getZoom() - 1, MIN_ZOOM) })
      return
    }
    if (hereMap) {
      const currentZoom = hereMap.getZoom()
      hereMap.setZoom(Math.max(currentZoom - 1, MIN_ZOOM), true)
    }
  }

  const zoomInButton = (
    <IconButton
      sx={{ p: 1 }}
      onPointerDown={(e) => {
        lastPointerWasTouch.current = e.pointerType === 'touch'
      }}
      onClick={handleZoomIn}
      aria-label="Zoom in"
      data-test-id="mapZoomInBtn"
    >
      <AddIcon fontSize="small" />
    </IconButton>
  )

  return (
    <Paper elevation={4}>
      <Stack direction="column" spacing={0}>
        {mlMap ? (
          <Tooltip
            title="At max zoom, all pins are shown"
            placement="left"
            open={tooltipOpen}
            onOpen={() => setTooltipOpen(true)}
            onClose={() => setTooltipOpen(false)}
          >
            {zoomInButton}
          </Tooltip>
        ) : (
          zoomInButton
        )}
        <Divider sx={{ mx: 0.5 }} />
        <IconButton
          sx={{ p: 1 }}
          onClick={handleZoomOut}
          aria-label="Zoom out"
          data-test-id="mapZoomOutBtn"
        >
          <HorizontalRuleIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  )
}
